import { useRef, useState } from 'react'
import { ImagePlus, Video, Loader2, X, Check } from 'lucide-react'
import { toast } from 'sonner'
import { uploadImage } from '@/features/admin/content/landingContentApi'
import { getYoutubeId } from '@/shared/utils/youtube'

interface MarkdownEditorProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  /** Clase de alto del textarea, ej. 'h-72' o 'h-[460px]'. */
  heightClass?: string
  /** Contenido extra alineado a la derecha de la barra (ej. botón Visualizar). */
  toolbarExtra?: React.ReactNode
}

/**
 * Editor de Markdown con barra de herramientas:
 * - "Imagen": sube un archivo al backend e inserta `![](url)` en el cursor.
 * - "Video": pide un enlace de YouTube y lo inserta (se renderiza embebido).
 * La inserción respeta la posición del cursor y rodea los bloques con saltos
 * de línea para que se rendericen correctamente.
 */
export function MarkdownEditor({
  value,
  onChange,
  placeholder,
  heightClass = 'h-72',
  toolbarExtra,
}: MarkdownEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [videoOpen, setVideoOpen] = useState(false)
  const [videoUrl, setVideoUrl] = useState('')

  const insertAtCursor = (snippet: string) => {
    const el = ref.current
    const start = el?.selectionStart ?? value.length
    const end = el?.selectionEnd ?? value.length
    const before = value.slice(0, start)
    const after = value.slice(end)
    const nlBefore = before && !before.endsWith('\n') ? '\n' : ''
    const nlAfter = after && !after.startsWith('\n') ? '\n' : ''
    const middle = `${nlBefore}${snippet}${nlAfter}`
    onChange(before + middle + after)
    requestAnimationFrame(() => {
      const pos = (before + middle).length
      el?.focus()
      el?.setSelectionRange(pos, pos)
    })
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    try {
      const url = await uploadImage(file)
      insertAtCursor(`![](${url})`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo subir la imagen')
    } finally {
      setUploading(false)
    }
  }

  const insertVideo = () => {
    const url = videoUrl.trim()
    if (!getYoutubeId(url)) {
      toast.error('Enlace de YouTube no válido')
      return
    }
    insertAtCursor(url)
    setVideoUrl('')
    setVideoOpen(false)
  }

  const btn = 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-ink-muted hover:bg-surface hover:text-ink disabled:opacity-50'

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 rounded-t-card border border-b-0 border-line bg-panel px-2 py-1.5">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className={btn}>
          {uploading ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
          {uploading ? 'Subiendo…' : 'Imagen'}
        </button>
        <button type="button" onClick={() => setVideoOpen((v) => !v)} className={btn}>
          <Video className="size-4" /> Video
        </button>
        {toolbarExtra && <div className="ml-auto">{toolbarExtra}</div>}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
      </div>

      {videoOpen && (
        <div className="flex items-center gap-2 border border-b-0 border-line bg-surface px-2 py-2">
          <input
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                insertVideo()
              }
            }}
            autoFocus
            placeholder="Pega el enlace de YouTube (https://www.youtube.com/watch?v=…)"
            className="min-w-0 flex-1 rounded-md border border-line bg-white px-2.5 py-1.5 text-sm text-ink outline-none focus:border-brand"
          />
          <button type="button" onClick={insertVideo} className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-dark">
            <Check className="size-3.5" /> Insertar
          </button>
          <button type="button" onClick={() => { setVideoOpen(false); setVideoUrl('') }} className="grid size-7 place-items-center rounded-md text-ink-subtle hover:text-ink">
            <X className="size-4" />
          </button>
        </div>
      )}

      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        className={`${heightClass} w-full resize-y rounded-b-card border border-line bg-white p-4 font-mono text-[13px] leading-relaxed text-ink outline-none focus:border-brand`}
      />
    </div>
  )
}
